import {useCallback, useEffect, useMemo, useState} from 'react';

import {useHierarchyDrill} from '../../hooks/useHierarchyDrill';
import {getAttribute} from '../../utils/user-access';
import {HierarchyItem, getHierarchyLevel} from './getHierarchyLevel';

export interface PickerItem extends HierarchyItem {
  name: string;
  rol?: string;
}

export interface UseUserPickerParams<T extends PickerItem> {
  items: T[];
  /** Reinicia el drill cuando cambia (p. ej. proyecto). */
  resetKey?: unknown;
  /** Restaura el trail de una selección previa. */
  initialParents?: string[] | null;
  /** Email a excluir de los niveles hijos. Default: el del JWT. */
  currentUserEmail?: string;
  /** Resolución de nivel inyectable; default = jerarquía managers+virtualManager. */
  getLevel?: (items: T[], parentId: string | null) => T[];
}

function sortByName<T extends {name: string}>(items: T[]): T[] {
  return [...items].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, {sensitivity: 'base'}),
  );
}

/**
 * Hook headless del selector de usuarios: dueño del estado (búsqueda, vista,
 * drill) y de la lista visible.
 *
 * La búsqueda es global: al escribir busca sobre la lista completa de
 * usuarios, ignorando el drill y la vista; al limpiar vuelve a la jerarquía
 * en el nivel actual.
 */
export function useUserPicker<T extends PickerItem>({
  items,
  resetKey,
  initialParents,
  currentUserEmail,
  getLevel,
}: UseUserPickerParams<T>) {
  const [searchString, setSearchString] = useState('');
  const [isHierarchyView, setIsHierarchyView] = useState(true);

  // Limpia la búsqueda al cambiar de contexto.
  useEffect(() => {
    setSearchString('');
  }, [resetKey]);

  const excludeEmail = currentUserEmail ?? getAttribute<string>('email', '');

  // El drill se reinicia al cambiar de proyecto o de vista, no al buscar.
  const drillResetKey = `${String(resetKey ?? '')}|${isHierarchyView}`;
  const {trailItems, currentParentId, drillDown, drillUp} = useHierarchyDrill(
    items,
    drillResetKey,
    initialParents,
  );

  const trimmedSearch = searchString.trim().toLowerCase();
  const isSearching = trimmedSearch.length > 0;

  const resolveLevel = useCallback(
    (parentId: string | null): T[] =>
      getLevel
        ? getLevel(items, parentId)
        : getHierarchyLevel(items, parentId, {excludeEmail}),
    [getLevel, items, excludeEmail],
  );

  const visibleItems = useMemo(() => {
    if (isSearching) {
      return sortByName(
        items.filter(item => item.name.toLowerCase().includes(trimmedSearch)),
      );
    }
    if (!isHierarchyView) {
      return sortByName(items);
    }
    return sortByName(resolveLevel(currentParentId));
  }, [
    isSearching,
    trimmedSearch,
    isHierarchyView,
    items,
    resolveLevel,
    currentParentId,
  ]);

  const toggleView = useCallback(() => setIsHierarchyView(prev => !prev), []);

  return {
    searchString,
    setSearchString,
    isHierarchyView,
    toggleView,
    isSearching,
    trailItems,
    currentParentId,
    drillDown,
    drillUp,
    visibleItems,
    excludeEmail,
  };
}
